import { useState, useRef, useCallback, useEffect } from "react";
import { Loader2, Mic, Square, Sparkles, FolderKanban, Link2, Upload, Mail, X, Copy, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCapture, type CaptureSourceType } from "@/hooks/use-capture";
import {
  useListProjects,
  getListProjectsQueryKey,
  getListInboxQueryKey,
  getGetTodayQueryKey,
} from "@workspace/api-client-react";
import RecordingWave from "./recording-wave";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { VOICE_CAPTURE_ENABLED } from "@/lib/voiceMode";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const NO_PROJECT = "__none__";

type CaptureMode = CaptureSourceType | "link" | "file";

const MODE_LABELS: Record<CaptureMode, string> = {
  text: "Texte",
  voice: "Voix",
  email: "Email",
  link: "Lien",
  file: "Fichier",
};

export interface QuickCaptureProps {
  variant?: "default" | "hero";
  placeholder?: string;
  className?: string;
  onCaptured?: () => void;
}

function isValidUrl(str: string): boolean {
  try {
    const url = new URL(str.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function CAPTURE_EMAIL(userId: string): string {
  return `${userId}@capture.onenext.app`;
}

export default function QuickCapture({
  variant = "default",
  placeholder,
  className = "",
  onCaptured,
}: QuickCaptureProps) {
  const capture = useCapture({ onCaptured });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<CaptureMode>("text");
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // DB user UUID (different from Clerk's user.id)
  const [dbUserId, setDbUserId] = useState<string | null>(null);

  const projectsQuery = useListProjects(
    { status: "active" },
    {
      query: {
        queryKey: getListProjectsQueryKey({ status: "active" }),
        staleTime: 60_000,
      },
    },
  );
  const projects = projectsQuery.data ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "link") {
      void handleUrlCapture();
    } else if (mode === "file") {
      fileInputRef.current?.click();
    } else {
      // text / voice / email modes — use the existing pipeline
      capture.submitText();
    }
  };

  // ── URL capture ────────────────────────────────────────────────────────
  const handleUrlCapture = async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!isValidUrl(url)) {
      toast({ title: "URL invalide", description: "Entrez une URL complète (https://…)", variant: "destructive" });
      return;
    }
    setUrlLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/inbox/capture-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error("Failed");
      toast({ title: "Lien capturé !", description: "Analyse en cours…" });
      setUrlInput("");
      queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
      onCaptured?.();
    } catch {
      toast({ title: "Erreur lors de la capture du lien", variant: "destructive" });
    } finally {
      setUrlLoading(false);
    }
  };

  // ── File capture ────────────────────────────────────────────────────────
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      const MAX_MB = 25;
      if (file.size > MAX_MB * 1024 * 1024) {
        toast({ title: `Fichier trop volumineux (max ${MAX_MB} Mo)`, variant: "destructive" });
        return;
      }
      setFileUploading(true);
      try {
        // Step 1: request presigned upload URL — backend expects { name, size, contentType }
        const urlRes = await fetch(`${API_BASE}/api/storage/uploads/request-url`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type || "application/octet-stream" }),
        });
        if (!urlRes.ok) throw new Error("Cannot get upload URL");
        const { uploadURL, objectPath } = (await urlRes.json()) as { uploadURL: string; objectPath: string };

        // Step 2: upload to object storage
        const uploadRes = await fetch(uploadURL, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!uploadRes.ok) throw new Error("Upload failed");

        // Step 3: read text content from file if possible
        let textContent = "";
        const isText =
          file.type.startsWith("text/") ||
          file.type === "application/json" ||
          /\.(txt|md|csv|tsv|json|log|xml|yaml|yml)$/i.test(file.name);
        if (isText && file.size < 500_000) {
          textContent = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve((reader.result as string).slice(0, 12_000));
            reader.onerror = () => resolve("");
            reader.readAsText(file);
          });
        }

        // Build rawText — include actual content for text files, metadata otherwise
        const rawText = textContent
          ? `Fichier: ${file.name}\nType: ${file.type || "inconnu"}\n\n--- Contenu ---\n${textContent}`
          : `Fichier: ${file.name}\nType: ${file.type || "inconnu"}\nTaille: ${(file.size / 1024).toFixed(1)} Ko\nChemin: ${objectPath}`;
        const inboxRes = await fetch(`${API_BASE}/api/inbox`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sourceType: "file", rawText }),
        });
        if (!inboxRes.ok) throw new Error("Cannot create inbox item");

        const createdItem = (await inboxRes.json()) as { id: string };

        // Step 4: trigger AI analysis (fire-and-forget)
        void fetch(`${API_BASE}/api/inbox/${createdItem.id}/analyze`, {
          method: "POST",
          credentials: "include",
        });

        toast({ title: "Fichier capturé !", description: file.name });
        queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
        onCaptured?.();
      } catch {
        toast({ title: "Erreur lors de l'upload", variant: "destructive" });
      } finally {
        setFileUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [toast, queryClient, onCaptured],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    void handleFiles(e.dataTransfer.files);
  };

  // Fetch DB user UUID (needed for the email capture address)
  useEffect(() => {
    if (mode !== "email" || dbUserId) return;
    fetch(`${API_BASE}/api/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { id: string } | null) => {
        if (data?.id) setDbUserId(data.id);
      })
      .catch(() => {});
  }, [mode, dbUserId]);

  const handleCopyEmail = async () => {
    if (!dbUserId) return;
    try {
      await navigator.clipboard.writeText(CAPTURE_EMAIL(dbUserId));
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      toast({ title: "Impossible de copier", variant: "destructive" });
    }
  };

  const isVoiceMode = (mode === "voice") && (capture.isRecording || capture.isTranscribing);

  const containerClass =
    variant === "hero"
      ? "p-5 md:p-6 rounded-3xl border border-border bg-card shadow-sm"
      : "p-4 rounded-2xl border border-border bg-card shadow-sm";

  const textareaClass =
    variant === "hero"
      ? "min-h-[100px] resize-none border-transparent bg-secondary/20 focus-visible:bg-background text-base"
      : "min-h-[80px] resize-none border-transparent bg-secondary/20 focus-visible:bg-background";

  const waveClass =
    variant === "hero" ? "min-h-[100px] w-full" : "min-h-[80px] w-full";

  // Sync mode → sourceType for the capture hook
  useEffect(() => {
    if (mode === "text" || mode === "voice" || mode === "email") {
      capture.setSourceType(mode);
    }
  }, [mode]);

  return (
    <form
      onSubmit={submit}
      className={`${containerClass} ${className}`}
      aria-label="Capture rapide"
    >
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <label
          htmlFor="quick-capture-textarea"
          className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          Capture rapide
        </label>
        <div className="flex items-center gap-2">
          {/* Project selector — hide in link/file/email modes */}
          {mode !== "link" && mode !== "file" && mode !== "email" && (
            <Select
              value={capture.projectId || NO_PROJECT}
              onValueChange={(v) => {
                if (v === NO_PROJECT) {
                  capture.setSelectedProject("", "");
                } else {
                  const p = projects.find((x) => x.id === v);
                  capture.setSelectedProject(v, p?.name ?? "");
                }
              }}
            >
              <SelectTrigger
                className="h-8 w-[160px] text-xs rounded-full"
                aria-label="Associer à un projet"
                data-testid="select-capture-project"
              >
                <FolderKanban className="w-3 h-3 mr-1 shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Aucun projet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>Aucun projet</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Mode selector */}
          <Select value={mode} onValueChange={(v) => setMode(v as CaptureMode)}>
            <SelectTrigger
              className="h-8 w-[120px] text-xs rounded-full"
              aria-label="Mode de capture"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">{MODE_LABELS.text}</SelectItem>
              {VOICE_CAPTURE_ENABLED && (
                <SelectItem value="voice">{MODE_LABELS.voice}</SelectItem>
              )}
              <SelectItem value="email">{MODE_LABELS.email}</SelectItem>
              <SelectItem value="link">{MODE_LABELS.link}</SelectItem>
              <SelectItem value="file">{MODE_LABELS.file}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── URL mode ── */}
      {mode === "link" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="url"
                placeholder="https://exemple.com/article…"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="pl-9 rounded-xl border-border/60"
                autoFocus
              />
            </div>
            {urlInput && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-10 w-10 shrink-0"
                onClick={() => setUrlInput("")}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          {urlInput && isValidUrl(urlInput) && (
            <p className="text-[11px] text-muted-foreground px-1">
              Le titre et la description de la page seront extraits automatiquement.
            </p>
          )}
        </div>
      )}

      {/* ── File mode ── */}
      {mode === "file" && (
        <div
          className={`relative rounded-xl border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-3 py-8 cursor-pointer ${
            isDragOver
              ? "border-primary bg-primary/5"
              : "border-border/60 hover:border-primary/50 hover:bg-secondary/30"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {fileUploading ? (
            <>
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Upload en cours…</p>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-muted-foreground/60" />
              <div className="text-center">
                <p className="text-sm font-medium">
                  Glissez un fichier ici
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  ou cliquez pour sélectionner · max 25 Mo
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground/60">
                PDF, images, documents…
              </p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.txt,.md,.csv"
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </div>
      )}

      {/* ── Email forward mode ── */}
      {mode === "email" && (
        <div className="space-y-3">
          <Textarea
            id="quick-capture-textarea"
            value={capture.text}
            onChange={(e) => capture.setText(e.target.value)}
            placeholder="Collez le contenu d'un email…"
            className={textareaClass}
            data-testid="quick-capture-input"
          />
          <div className="rounded-xl border border-border/60 bg-secondary/30 p-3 space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Adresse de transfert
              </p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0 rounded-lg bg-background border border-border/60 px-3 py-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <code className="text-xs text-foreground truncate flex-1">
                    {dbUserId ? CAPTURE_EMAIL(dbUserId) : "Chargement…"}
                  </code>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 text-xs px-3 shrink-0"
                  onClick={handleCopyEmail}
                >
                  {emailCopied ? (
                    <CheckCheck className="w-3.5 h-3.5 mr-1.5 text-green-500" />
                  ) : (
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {emailCopied ? "Copié !" : "Copier"}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Transférez n'importe quel email à cette adresse pour l'ajouter automatiquement à votre Inbox.
              </p>
            </div>
        </div>
      )}

      {/* ── Voice mode ── */}
      {mode === "voice" && isVoiceMode && (
        <RecordingWave
          isTranscribing={capture.isTranscribing}
          className={waveClass}
        />
      )}

      {/* ── Text mode (default) ── */}
      {mode === "text" && (
        <Textarea
          id="quick-capture-textarea"
          value={capture.text}
          onChange={(e) => capture.setText(e.target.value)}
          placeholder={placeholder ?? "Une idée, une tâche, un rappel…"}
          className={textareaClass}
          data-testid="quick-capture-input"
        />
      )}

      {/* ── Voice idle state (textarea shown) ── */}
      {mode === "voice" && !isVoiceMode && (
        <Textarea
          id="quick-capture-textarea"
          value={capture.text}
          onChange={(e) => capture.setText(e.target.value)}
          placeholder="Notez ce que vous diriez à voix haute…"
          className={textareaClass}
          data-testid="quick-capture-input"
        />
      )}

      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 mt-3">
        {/* Left side: voice toggle (only in voice/text/email modes) */}
        {VOICE_CAPTURE_ENABLED &&
        (mode === "text" || mode === "voice" || mode === "email") ? (
          <Button
            type="button"
            variant={capture.isRecording ? "destructive" : "secondary"}
            size="sm"
            className="rounded-full"
            onClick={capture.toggleRecording}
            disabled={capture.isTranscribing || mode === "email"}
            aria-label={
              capture.isRecording
                ? "Arrêter l'enregistrement"
                : "Démarrer l'enregistrement vocal"
            }
          >
            {capture.isTranscribing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : capture.isRecording ? (
              <Square className="w-4 h-4 mr-2 fill-current" />
            ) : (
              <Mic className="w-4 h-4 mr-2" />
            )}
            {capture.isRecording ? "Arrêter" : "Enregistrer"}
          </Button>
        ) : (
          <div />
        )}

        {/* Right side: submit button */}
        {mode === "link" && (
          <Button
            type="submit"
            disabled={!urlInput.trim() || !isValidUrl(urlInput) || urlLoading}
            className="rounded-full"
          >
            {urlLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            Capturer le lien
          </Button>
        )}

        {mode === "file" && (
          <Button
            type="button"
            disabled={fileUploading}
            className="rounded-full"
            onClick={() => fileInputRef.current?.click()}
          >
            {fileUploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Choisir un fichier
          </Button>
        )}

        {(mode === "text" || mode === "voice" || mode === "email") && (
          <Button
            type="submit"
            disabled={!capture.text.trim() || capture.isPending}
            className="rounded-full"
            data-testid="quick-capture-submit"
          >
            {capture.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Capturer et analyser
          </Button>
        )}
      </div>
    </form>
  );
}
